import i18n, { type Resource } from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'

import { syncDateFnsLocale } from './date-locale'
import { BUSINESS_LANGUAGES } from './locale-resolution'

// Eager, synchronous bundling — no HTTP backend. Every JSON file under
// locales/<lng>/ is one namespace (file name = namespace, folder = language).
// Vite inlines these at build time, so resources are ready before the first
// render and no Suspense is needed. Add a new locale by dropping in its folder.
//
// pl and uk are internal locales (see INTERNAL_LANGUAGES below), enabled for
// local development and staging only: their catalogs are globbed separately and
// only in those modes, so pre-prod/production bundles don't ship them at all.
//
// Plain `===` on purpose: Vite inlines MODE, and the bundler only drops the
// dead glob below when this folds to a literal `false` - an `[...].includes()`
// check doesn't fold, and pl/uk leak into every bundle.
const INTERNAL_LOCALES_ENABLED =
  import.meta.env.MODE === 'development' || import.meta.env.MODE === 'staging'

type LocaleModules = Record<string, { default: Record<string, unknown> }>

const modules: LocaleModules = {
  ...import.meta.glob<{ default: Record<string, unknown> }>(
    ['./locales/*/*.json', '!./locales/{pl,uk}/*.json'],
    { eager: true },
  ),
  ...(INTERNAL_LOCALES_ENABLED
    ? import.meta.glob<{ default: Record<string, unknown> }>('./locales/{pl,uk}/*.json', {
        eager: true,
      })
    : {}),
}

const resources = Object.entries(modules).reduce<Resource>((acc, [path, module]) => {
  const match = path.match(/\/locales\/([^/]+)\/([^/]+)\.json$/)
  if (!match) return acc

  const [, language, namespace] = match
  acc[language] ??= {}
  ;(acc[language] as Record<string, unknown>)[namespace] = module.default
  return acc
}, {})

// English is the source of truth (types are generated from it) and is authored
// as American English — our target market is the US, matching the hardcoded
// 'en-US' already used for numbers/currency/plurals (src/utils/number,
// src/utils/currency, src/utils/intl) and the enUS date-fns locale (see
// ./date-locale). Region-qualified ("en-US" rather than bare "en") because a
// genuinely divergent "en-GB" catalog is coming later - see docs/i18n/README.md.
// fr-CA and es-US are the business locales, generated in-repo by the translation
// pipeline (see docs/i18n/README.md §9); they're region-qualified because they're
// genuinely divergent variants (Canadian French, US Spanish), not just a
// generic language bucket - the former bare "fr" sample is now fr-CA.
// BUSINESS_LANGUAGES lives in ./locale-resolution, which the backend-driven
// language rules share.

// Internal-only: translated in-repo from time to time (not yet by the
// translation pipeline), and bundled only in local development and on staging.
// The Language picker no longer offers them: its options come from the
// building's availableLocale, which only ever lists BUSINESS_LANGUAGES.
// Keep in sync with the glob exclusion above.
const INTERNAL_LANGUAGES = ['pl', 'uk'] as const

export const SUPPORTED_LANGUAGES: readonly string[] = INTERNAL_LOCALES_ENABLED
  ? [...BUSINESS_LANGUAGES, ...INTERNAL_LANGUAGES]
  : BUSINESS_LANGUAGES

// Namespaces are the JSON file names, taken from the source locale.
const namespaces = Object.keys(resources['en-US'] ?? {})

// Detection: automatic browser-language (navigator) detection is intentionally
// disabled until the i18n migration is complete - untranslated routes would
// otherwise silently switch non-English browsers away from the only fully
// authored locale (en-US). Once logged in, the backend decides: UserLocaleSync
// applies the account's language as gated by the building's flags (user.locale /
// building.i18nAvailable / availableLocale / locale - see ./locale-resolution),
// and the User Panel → Profile picker saves user.locale. The applied language is
// cached to localStorage, so the first render after a reload is already in it.
// Before any of that, fallbackLng ('en-US', i.e. American English - see
// SUPPORTED_LANGUAGES above) wins.
// TODO: add 'navigator' back to detection.order once migration is complete.
// `load: 'currentOnly'` loads only the exact resolved language - not
// 'languageOnly', which would strip "en-US" down to "en" before matching and
// find no resource bucket there anymore now that locales are region-qualified.
// i18next's own language negotiation still does family-matching regardless of
// `load`: an unsupported variant of a supported language (e.g. "en-GB" before
// it's added, "fr-CA") still resolves to the closest supported code, and
// anything unrelated (e.g. "de-DE") falls through to fallbackLng - verified via
// a throwaway i18next instance before making this change.
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    defaultNS: 'common',
    detection: {
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
      order: ['localStorage'],
    },
    fallbackLng: 'en-US',
    interpolation: { escapeValue: false },
    keySeparator: '.',
    load: 'currentOnly',
    ns: namespaces,
    nsSeparator: ':',
    react: { useSuspense: false },
    resources,
    // Non-English catalogs hold `""` placeholders for keys not translated yet (i18n:extract's own convention for untranslated keys).
    // false makes i18next treat `""` the same as a missing key - it falls
    // through the fallbackLng chain to the real en-US text instead of
    // rendering blank - and self-resolves once the value is translated.
    // Load-bearing, not cosmetic: verified against i18next 26.3.4 that the
    // default (true) returns "" rather than falling back.
    returnEmptyString: false,
    returnNull: false,
    supportedLngs: SUPPORTED_LANGUAGES,
  })

// Keep date-fns' formatting locale aligned with the resolved i18next language.
syncDateFnsLocale(i18n)

export default i18n
